import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  aberturaDoCard,
  cardsDoCurso,
  formatarData,
  formatarMesAno,
  linkDeAssinaturaServe,
  useHub,
  type CardVitrine,
  type CursoDoAluno,
} from "../lib/dados";
import Carregando from "../componentes/Carregando";
import Rodape from "../componentes/Rodape";
import AvisoContrato from "../componentes/AvisoContrato";
import {
  Aviso,
  ChipDoCard,

  IconeExterno,
  EscudoFBC,
  LinkDoCard,
  SetaDireita,
  Topo,
} from "../componentes/ui";
import { IconeDoCard } from "../componentes/icones";
import FaculdadeNotas from "../componentes/FaculdadeNotas";
import FaculdadeHistorico from "../componentes/FaculdadeHistorico";
import FaculdadeSecretaria from "../componentes/FaculdadeSecretaria";

// A Área da Faculdade — a FBC.
//
// É o único lugar do hub em que a página inteira é azul-marinho, e isso é a
// separação que o dono do produto pediu: a faculdade tem matrícula, turma,
// secretaria e notas, e não é "mais um curso" na mesma prateleira dos cursos
// livres. Entrar aqui tem de parecer cruzar para outro prédio, mantendo a mesma
// marca — mesmas fontes, mesmo laranja, mesmo radius.
//
// Sobre navy, texto secundário usa `navy-mute` (#aab4c8). O cinza da marca
// (#727376) reprova em contraste sobre #102850 e não pode ser usado aqui.
type Aba = "visao" | "secretaria" | "notas" | "historico";

const ABAS: Aba[] = ["visao", "secretaria", "notas", "historico"];

// A aba é SUB-ROTA (`/faculdade/:aba`), e não `useState`.
//
// É a doutrina da casa desde o HUB DP, e vale por três razões medidas: o aluno
// volta pelo botão do navegador, compartilha o link de uma aba, e recarregar não
// o joga para a primeira. Estado que deveria estar no endereço, guardado em
// memória, é uma tela que não se pode indicar a ninguém — e aqui a Secretaria é
// justamente o que o suporte precisa mandar por link.
export default function Faculdade() {
  const estado = useHub();
  const { aba: abaDaUrl } = useParams();
  const [params] = useSearchParams();
  const navegar = useNavigate();
  // O curso chega pela QUERY porque os cards "Secretaria do Aluno" e "Minhas
  // Notas" abrem aqui a partir da página de UM curso, e `aberturaDoCard` põe
  // `?curso=` em todo card de escopo 'curso'. Sem semear, quem tem mais de uma
  // matrícula cairia na Secretaria da OUTRA pós — medido em 09/09: 1.446 alunos
  // têm duas ou mais.
  //
  // O `?? hub.faculdade[0]` abaixo cobre curso que a pessoa não tem: query é
  // texto de fora, e não vira estado sem passar pela lista dela.
  const [slugAtivo, setSlugAtivo] = useState<string | null>(params.get("curso"));

  const aba: Aba = ABAS.includes(abaDaUrl as Aba) ? (abaDaUrl as Aba) : "visao";

  if (estado.fase === "carregando") return <Carregando />;
  if (estado.fase === "erro") {
    return (
      <Aviso titulo="Algo deu errado">
        Não conseguimos carregar a sua faculdade agora. Recarregue a página; se continuar, fale com o
        suporte.
      </Aviso>
    );
  }
  if (estado.fase === "sem-vinculo") {
    return (
      <Aviso titulo="Não encontramos o seu cadastro" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Nenhuma compra está ligada a este e-mail.
      </Aviso>
    );
  }

  const { hub } = estado;

  if (hub.faculdade.length === 0) {
    return (
      <div className="min-h-screen">
        <Topo atual="faculdade" nome={hub.nome} avatarUrl={hub.avatarUrl} />
        {/* "Você ainda não tem curso" é falso para quem se FORMOU, e esta tela
            não sabe a diferença: `hub.faculdade` sai de `vw_meus_cursos`, que
            parte de `vw_acesso_efetivo` — o formado e o que nunca comprou
            chegam aqui iguais. Em vez de adivinhar, a tela nomeia os dois
            caminhos e deixa o acervo alcançável (000281). */}
        <Aviso
          titulo="Nenhum curso da faculdade ativo agora"
          acao={<Link to="/" className="pill-cta">Ver os meus cursos</Link>}
        >
          A FBC — Faculdade Brasileira de Contabilidade é onde ficam o MBA, a Formação em Perícia e as
          pós-graduações, com matrícula, secretaria e notas. Se você já concluiu um curso aqui, os
          seus documentos continuam em{" "}
          <Link to="/meus-documentos" className="font-semibold text-link underline-offset-4 hover:underline">
            Meus documentos
          </Link>
          . Se você se matriculou e o curso não aparece, fale com o suporte que a gente confere.
        </Aviso>
      </div>
    );
  }

  const curso = hub.faculdade.find((c) => c.slug === slugAtivo) ?? hub.faculdade[0];
  const cards = cardsDoCurso(hub.cardsDeCurso, curso.slug);
  const aulas = cards.find((c) => c.card_slug === "aulas");
  // Secretaria e Minhas Notas têm aba própria; não se repetem na visão geral.
  const naVisao = cards.filter(
    (c) => !["aulas", "secretaria-do-aluno", "minhas-notas"].includes(c.card_slug),
  );

  const abas: Array<{ id: Aba; rotulo: string }> = [
    { id: "visao", rotulo: "Visão geral" },
    { id: "secretaria", rotulo: "Secretaria" },
    // O ROTULO é "Avaliações" (pedido de Victor em 16/09) e o id da rota
    // continua `notas`: `/faculdade/notas` está em link compartilhado, no card
    // da vitrine e na prova de tela, e trocar o endereço por causa do nome
    // quebraria os três sem ganhar nada — o rótulo é o que o aluno lê.
    { id: "notas", rotulo: "Avaliações" },
    // Aba IRMÃ de "Avaliações", e não sub-aba dentro dela: pedido da reunião
    // de 31/08 ("histórico de tentativas em uma aba separada"). Aba irmã é
    // sub-rota de um nível — `/faculdade/historico` —, então o link é
    // compartilhável e o botão do navegador funciona. Sub-aba exigiria um
    // segundo segmento e daria dois controles para a mesma escolha.
    { id: "historico", rotulo: "Histórico" },
  ];

  return (
    // A COR separada da IMAGEM, e as duas juntas — nunca só o gradiente.
    //
    // `bg-[linear-gradient(…)]` define `background-image` e deixa
    // `background-color` em `transparent`. Visualmente dá no mesmo (o gradiente
    // é opaco e cobre tudo), e o preço é que a Faculdade inteira fica
    // IMPOSSÍVEL de medir: toda sonda de contraste sobe os pais procurando cor
    // de fundo, não acha nada aqui, e cai no fundo da PÁGINA — que é claro no
    // tema claro. É a cicatriz de 25/08, que consertou `painel-navy` e
    // `trilho-navy` no `index.css` e não alcançou este gradiente inline.
    //
    // Medido em 22/09, no card da Secretaria: sem a cor, a sonda dizia 1,79 : 1
    // para o texto secundário e 3,73 : 1 para o título — números de um branco
    // que não está na tela. Com ela, 7,58 e 12,88, que são os verdadeiros.
    //
    // `#102850` é o tom do MEIO do gradiente: é o que está atrás do conteúdo,
    // onde os cards ficam.
    <div className="min-h-screen bg-[#102850] bg-[linear-gradient(180deg,#0c1f40_0%,#102850_40%,#0e2348_100%)]">
      <Topo atual="faculdade" nome={hub.nome} avatarUrl={hub.avatarUrl} />

      <div className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="flex items-center gap-4 pt-10">
          <EscudoFBC />
          <div>
            <h1 className="text-3xl font-extrabold text-white sm:text-4xl">Minha Faculdade</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-navy-mute">
              Faculdade Brasileira de Contabilidade
            </p>
            <div className="mt-2 h-[3px] w-16 rounded-sm bg-laranja" />
          </div>
        </div>

        {/* Quem tem mais de uma matrícula troca de curso aqui. Pills, não select:
            com dois ou três cursos a lista inteira à vista é mais fácil que um
            campo que esconde as opções. */}
        {hub.faculdade.length > 1 && (
          <div className="mt-7 flex flex-wrap gap-2">
            {hub.faculdade.map((c) => (
              <button
                key={c.slug}
                // `data-curso` e não busca por TEXTO: o nome de uma pós tem 74
                // caracteres e aparece também na carteirinha e no contrato, e
                // seletor por texto sem escopo pega o elemento errado — a
                // cicatriz dos sete rótulos iguais da calculadora. Atributo de
                // escopo custa nada.
                data-curso={c.slug}
                onClick={() => setSlugAtivo(c.slug)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                  c.slug === curso.slug
                    ? "border-laranja bg-laranja text-azul"
                    : "border-navy-ink/15 text-navy-mute hover:bg-navy-ink/5 hover:text-navy-ink"
                }`}
              >
                {c.nome}
              </button>
            ))}
          </div>
        )}

        <Carteirinha nome={hub.nome} curso={curso} />

        {curso.trancado && (
          <AvisoContrato curso={curso} urlContrato={hub.contratoUrlPorCurso.get(curso.slug)} />
        )}

        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Áreas da faculdade">
          {abas.map((a) => (
            <button
              key={a.id}
              id={`aba-botao-${a.id}`}
              role="tab"
              aria-selected={aba === a.id}
              aria-controls={`aba-${a.id}`}
              onClick={() => navegar(a.id === "visao" ? "/faculdade" : `/faculdade/${a.id}`)}
              className={`min-h-11 rounded-full border px-6 py-3 font-display font-semibold transition ${
                aba === a.id
                  ? "border-laranja bg-laranja text-azul"
                  : "border-navy-ink/15 text-navy-mute hover:bg-navy-ink/5 hover:text-navy-ink"
              }`}
            >
              {a.rotulo}
            </button>
          ))}
        </div>

        {/* `role="tabpanel"` com `aria-labelledby` fecha o par que faltava: sem
            ele o leitor de tela anuncia as abas mas não sabe dizer que região
            elas comandam. */}
        <div className="mt-7" role="tabpanel" id={`aba-${aba}`} aria-labelledby={`aba-botao-${aba}`}>
          {aba === "visao" && (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {aulas && (
                  <AulasFBC card={aulas} curso={curso.slug} nomeCurso={curso.nome} travado={curso.trancado} />
                )}
                {naVisao.map((card, i) => (
                  <CardNavy key={card.card_slug} card={card} curso={curso.slug} i={i + 2} travado={curso.trancado} />
                ))}
              </div>
              {naVisao.length === 0 && !aulas && (
                <p className="text-navy-mute">Nenhum recurso liberado para este curso ainda.</p>
              )}
            </>
          )}

          {aba === "secretaria" && (
            <>
              {/* Os contratos vêm ANTES do balcão, e a ordem é do produto: contrato
                  pendente TRANCA a vitrine do curso (000044-000047), então é o que
                  o aluno precisa resolver primeiro. Documento e pedido vêm depois,
                  porque nenhum deles destranca nada. */}
              <Contratos curso={curso} contratoUrlPorCurso={hub.contratoUrlPorCurso} />
              <div className="mt-9">
                <FaculdadeSecretaria
                  cursoAtivo={curso.slug}
                  cursos={hub.faculdade.map((c) => ({ slug: c.slug, nome: c.nome }))}
                />
              </div>
            </>
          )}
          {aba === "notas" && <FaculdadeNotas cursoAtivo={curso.slug} />}
          {aba === "historico" && <FaculdadeHistorico cursoAtivo={curso.slug} />}
        </div>

        {/* Sem linha própria de "FBC — Faculdade Brasileira de Contabilidade"
            aqui: com o rodapé abaixo, eram dois separadores seguidos e um vão
            vazio entre eles. O brasão no topo da página já nomeia a faculdade. */}
        <Rodape sobreNavy />
      </div>
    </div>
  );
}

/**
 * A carteirinha do aluno: nome, curso, turma e situação da matrícula.
 *
 * É a peça de identidade da área — um objeto de orgulho, não um widget de dados.
 * `previsao_termino` aparece só quando existe: a medição de 12/08 encontrou a
 * data em 252 das 39.822 matrículas, então uma carteirinha que a exigisse
 * mostraria "—" para quase todo mundo.
 */
function Carteirinha({ nome, curso }: { nome: string | null; curso: CursoDoAluno }) {
  const m = curso.matricula;
  const situacao =
    m?.status === "ativa"
      ? { texto: "Matrícula ativa", cor: "text-sucesso-claro" }
      : m?.status === "concluida"
        ? { texto: "Curso concluído", cor: "text-navy-ink" }
        : m?.status === "cancelada"
          ? { texto: "Matrícula cancelada", cor: "text-laranja-claro" }
          : null;

  return (
    <div className="tv-card rise mt-7 flex flex-wrap justify-between gap-6 p-6 sm:p-7">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.16em] text-navy-mute">Carteirinha do aluno</p>
        <p className="mt-1.5 font-display text-xl font-semibold text-white">{nome ?? "Aluno"}</p>
        <p className="mt-0.5 text-navy-mute">{curso.nome}</p>

        <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-3 text-sm">
          {m?.turma && <ItemCarteirinha rotulo="Turma" valor={m.turma} />}
          {situacao && <ItemCarteirinha rotulo="Situação" valor={situacao.texto} cor={situacao.cor} />}
          {m?.inicio && <ItemCarteirinha rotulo="Início" valor={formatarData(m.inicio)} />}
          {m?.previsao_termino && (
            <ItemCarteirinha rotulo="Término previsto" valor={formatarMesAno(m.previsao_termino)} />
          )}
        </dl>
      </div>
      {/* No celular o selo da carteirinha some: com o wrap ele cai para baixo
          dos dados e fica solto, e o brasão do cabeçalho já está logo acima. */}
      <div className="hidden flex-col items-center justify-center gap-2 sm:flex">
        <EscudoFBC />
      </div>
    </div>
  );
}

function ItemCarteirinha({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.08em] text-navy-mute">{rotulo}</dt>
      <dd className={`mt-0.5 font-semibold tabular-nums ${cor ?? "text-navy-ink"}`}>{valor}</dd>
    </div>
  );
}

function AulasFBC({
  card,
  curso,
  nomeCurso,
  travado,
}: {
  card: CardVitrine;
  curso: string;
  nomeCurso: string;
  travado?: boolean;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <div>
        <h2 className="text-xl font-bold text-white">Aulas da pós-graduação</h2>
        <p className="mt-1 max-w-md text-navy-mute">
          {card.descricao ?? `Assista às aulas de ${nomeCurso} no portal do curso.`}
        </p>
      </div>
      {abre ? (
        <span className="pill-cta shrink-0">
          Ir para as aulas <SetaDireita />
          {!abertura.interno && <span className="sr-only">(abre em outra aba)</span>}
        </span>
      ) : (
        <ChipDoCard card={{ trancado: card.trancado || travado }} sobreNavy />
      )}
    </>
  );

  const base =
    "tv-card rise flex min-h-32 flex-wrap items-center justify-between gap-4 p-6 sm:col-span-2 lg:col-span-3";
  const estilo = { "--i": 1 } as React.CSSProperties;

  // `LinkDoCard` e não `<a target="_blank">` cravado: quem decide aba e componente é ELE, num
  // lugar só (a regra da 000065). Esta tela nasceu quando toda pós só tinha card de fora, e o
  // `target="_blank"` ficou escrito à mão aqui — então a Peric.IA, que é card INTERNO de uma
  // pós, abria numa aba nova recarregando o bundle inteiro. Victor viu em 24/08.
  return (
    <LinkDoCard abertura={abertura} className={base} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

function CardNavy({
  card,
  curso,
  i,
  travado,
}: {
  card: CardVitrine;
  /** O curso da tela: é quem resolve a escola do Memberkit na ponte /abrir. */
  curso: string;
  i: number;
  travado?: boolean;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-laranja/15 text-laranja-claro">
          <IconeDoCard slug={card.card_slug} />
        </span>
        {abre ? (
          // O ícone de "abre fora" só aparece quando ABRE fora. Card interno com seta de
          // link externo promete outra aba e entrega navegação na mesma — mentira pequena,
          // mas é a que faz o aluno abrir com o botão do meio e duplicar o hub.
          <span className="text-navy-mute">
            {abertura.interno ? <SetaDireita /> : <IconeExterno />}
          </span>
        ) : (
          <ChipDoCard card={{ trancado: card.trancado || travado }} sobreNavy />
        )}
      </div>
      <h3 className="mt-3 font-display font-semibold text-white">{card.card_nome}</h3>
      {card.descricao && <p className="mt-1 text-sm text-navy-mute">{card.descricao}</p>}
      {card.card_tipo === "curso_bonus" && (
        <span className="mt-2 inline-flex w-fit items-center rounded-full bg-laranja/15 px-2.5 py-0.5 text-xs font-semibold text-laranja-claro">
          Curso bônus
        </span>
      )}
    </>
  );

  const base = "tv-card rise flex min-h-32 flex-col p-5";
  const estilo = { "--i": i } as React.CSSProperties;

  // Sem `opacity`: sobre navy ela achatava o texto junto com o vidro do cartão
  // contra o fundo e derrubava o contraste. O chip "Em breve" já diz o estado.
  // E `LinkDoCard` decide a aba — ver o comentário no herói de aulas acima.
  return (
    <LinkDoCard abertura={abertura} className={base} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

/**
 * Contrato de matrícula — a primeira parte da aba Secretaria.
 *
 * Mostra o contrato do CURSO SELECIONADO, e não os de todos. Correção de Victor
 * em 31/08: a aba inteira respeita o seletor de curso do topo — o boletim e o
 * balcão já respeitavam, e só os contratos listavam tudo. Quem tem oito cursos
 * de faculdade via oito linhas de contrato ao entrar, com sete que não têm nada
 * a ver com o que ele está olhando.
 *
 * O curso aparece MESMO SEM linha de contrato no banco, e essa parte não muda. A
 * distinção importa desde a 000045: dos 77 mil acessos vigentes, 98% **não têm
 * linha de contrato** — a carga do Bubble trouxe acesso e não trouxe contrato —,
 * e a regra passou a tratar ausência como não assinado. Esconder quem não tem
 * linha ocultaria justamente quem está com o curso trancado por falta de
 * assinatura.
 *
 * Desde 14/08 (000049) os links reais da ZapSign estão no catálogo, e o contrato
 * pendente ganha o botão "Assinar contrato" — quando o link do curso serve
 * (`linkDeAssinaturaServe`). Onde ainda é placeholder, o texto continua mandando
 * falar com o suporte, porque CTA para link que não assina é pior que instrução.
 *
 * O BALCÃO — enviar documento, pedir declaração, ver os emitidos — saiu daqui em
 * 31/08 e virou `FaculdadeSecretaria`, com dado de verdade. Antes esta seção
 * mostrava o card "Secretaria do Aluno" em estado `em-breve` e um cartão dizendo
 * para falar com o suporte.
 */
function Contratos({
  curso,
  contratoUrlPorCurso,
}: {
  curso: CursoDoAluno;
  contratoUrlPorCurso: Map<string, string>;
}) {
  const cursos = [curso];
  return (
    <>
      <h2 className="mb-4 text-lg font-bold text-white">Contrato de matrícula</h2>
      {cursos.length === 0 ? (
        <div className="tv-card p-6 text-navy-mute">
          Nenhum curso da faculdade na sua conta.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {cursos.map((c, i) => (
            <div
              key={c.slug}
              className={`tv-card rise flex flex-wrap items-center gap-4 p-5 ${
                c.contratoPendente ? "border-laranja/40" : ""
              }`}
              style={{ "--i": i + 1 } as React.CSSProperties}
            >
              <div className="min-w-56 flex-1">
                <h3 className="font-display font-semibold text-white">
                  Contrato de matrícula — {c.nome}
                </h3>
                <p className="mt-0.5 text-sm tabular-nums text-navy-mute">
                  {!c.contratoPendente && c.contrato?.assinado_em
                    ? `Assinado em ${formatarData(c.contrato.assinado_em)}`
                    : c.trancado
                      ? "Aguardando assinatura — as aulas e as ferramentas liberam depois dela."
                      : "Aguardando assinatura — a secretaria envia o link por e-mail."}
                </p>
              </div>
              {!c.contratoPendente ? (
                <span className="inline-flex items-center gap-1.5 font-semibold text-sucesso-claro">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  Assinado
                </span>
              ) : linkDeAssinaturaServe(contratoUrlPorCurso.get(c.slug)) ? (
                <a
                  href={contratoUrlPorCurso.get(c.slug)}
                  target="_blank"
                  rel="noreferrer"
                  className="pill-cta"
                >
                  Assinar contrato <SetaDireita />
                  <span className="sr-only">(abre em outra aba)</span>
                </a>
              ) : (
                <span className="inline-flex items-center rounded-full bg-laranja/15 px-3 py-1 text-xs font-semibold text-laranja-claro">
                  Pendente
                </span>
              )}
            </div>
          ))}
        </div>
      )}

    </>
  );
}
